import React, { createRef, PureComponent, ReactElement, RefObject } from 'react';

import StipulatorContext, { IeTipoEstipulante } from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { isContrato, shouldRenderMobile } from '@philips-emr/hpms-stipulator-commons/lib/utils/utils';
import { Link, LinkProps } from '@philips-emr/tws-components';
import { AwesomeComponent, ComponentType, Oswald } from '@philips-emr/tws-core';
import { applicationContext } from '@philips-emr/tws-foundation';
import { classNames } from '@philips-emr/tws-utils';

import './hpms-dashboard-shourtcut.css';

interface LinkPropsDashboard extends LinkProps {
  module?: string;
  twsFunction?: string;
  requiredCount?: boolean;
}

interface ListMapRef {
  link: RefObject<HTMLAnchorElement>;
  dot: RefObject<HTMLLIElement>;
}

@AwesomeComponent({ type: ComponentType.DATA_ENTRY })
export default class HpmsDashboardShourtcut extends PureComponent {

  private wrapperShortcut = createRef<HTMLDivElement>();
  private refsItens: ListMapRef[] = [];

  private readonly expressaoRelatorio = 'stipulator.relatorios';

  private renderButtonAction(): ReactElement {
    const shortcuts = this.getShortcuts();

    // maxAction = 5 - total de sempre visiveis
    const maxAction = this.isPJ() ? 2 : 3;
    let current = 0;

    const somentePJ = [
      'inclusao-beneficiario',
      'exclusao-beneficiario',
      'reinclusao-beneficiario',
      'movimentacao-lote',
      'folha-pagamento',
      'historico-solicitacoes',
      'inconsistencia-sib',
      'analise-atendimentos',
      'historico-analises',
    ];

    const somenteContrato = [
      'mensalidades',
      'faturas',
      'inconsistencia-sib',
      'analise-atendimentos',
      'historico-analises',
    ];

    return (
      <>
        {
          shortcuts.filter((prop) => {
            if (somentePJ.includes(prop.module ?? '') && !this.isPJ()
                || somenteContrato.includes(prop.module ?? '') && !isContrato()) {
              return false;
            }

            if ((!prop.twsFunction && !prop.requiredCount) ||
              (prop.twsFunction == 'historico-solicitacoes' && this.isPJ())) {
              return true;
            }

            if (current < maxAction && (Oswald.isDesigning() || prop.requiredCount || this.hasPermission(prop.twsFunction))) {
              ++current;
              return true;
            }

            return false;
          })
            .map((prop, index) => {
              const refLink = this.createRefHtmlAnchor();
              this.refsItens.push({ link: refLink, dot: this.createRefHtmlLi() });

              return (
                <>
                  <Link ref={refLink} key={index} className={this.classNameLink(prop.value)} onClick={() => this.openModuleOnClick(prop)} {...prop} />
                  <div className="hpms-dashboard-shourtcut__divider" />
                </>
              );
            })
        }
      </>
    );
  }

  private classNameLink(value: string | undefined): string {
    return classNames({
      'hpms-dashboard-shourtcut__link': true,
      'hpms-dashboard-shourtcut__link__svg--size-relatorio': value === this.expressaoRelatorio,
    });
  }

  private openModuleOnClick({ module }: LinkPropsDashboard): void {
    module && applicationContext.openModule(module);
  }

  private onClickGotoAction(index: number): void {
    if (this.wrapperShortcut.current) {
      const element = this.refsItens[index].link.current;

      if (!element) {
        return;
      }

      const react = element.getBoundingClientRect();
      this.wrapperShortcut.current.scrollLeft += react.left;
    }
  }

  public componentDidMount(): void {
    this.wrapperShortcut.current ?.addEventListener('scroll', () => this.updateListDot());

    this.updateListDot();
  }

  private updateListDot(): void {
    const dotActive = 'hpms-dashboard-shourtcut__dot-item--active';

    this.refsItens.forEach(({ link, dot }) => {
      if (this.isInViewport(link.current)) {
        dot.current ?.classList.add(dotActive);
      } else {
        dot.current ?.classList.remove(dotActive);
      }
    });
  }

  public render(): ReactElement | null {
    const isRenderMobile = shouldRenderMobile();

    const className = classNames({
      ['hpms-dashboard-shourtcut']: true,
      ['hpms-dashboard-shourtcut-mobile']: isRenderMobile,
      ['hpms-dashboard-shourtcut-desktop']: !isRenderMobile,
    });

    return (
      <>
        <div className={className} ref={this.wrapperShortcut}>
          {this.renderBlankDiv()}
          {this.renderButtonAction()}
          {this.renderBlankDiv()}
        </div >

        {isRenderMobile && this.renderDots()}
      </>
    );
  }

  private renderBlankDiv(): ReactElement | null {
    if (shouldRenderMobile()) {
      return (<div className="hpms-dashboard-shourtcut-mobile__blank-div"></div>);
    }

    return null;
  }

  private renderDots(): ReactElement {
    return (
      <ul className="hpms-dashboard-shourtcut__dot">
        {this.refsItens.map(({ dot }, index) => <li onClick={() => this.onClickGotoAction(index)} ref={dot} className="hpms-dashboard-shourtcut__dot-item" key={index}></li>)}
      </ul>
    );
  }

  private isInViewport(element: HTMLAnchorElement | null): boolean {
    if (!element) {
      return false;
    }

    const rect = element.getBoundingClientRect();

    return (
      rect.top >= 0 && rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  private hasPermission(twsFunction: string | undefined): boolean {
    return !!twsFunction && StipulatorContext.isAllowedTwsFunction(twsFunction);
  }

  private createRefHtmlAnchor(): RefObject<HTMLAnchorElement> {
    return createRef<HTMLAnchorElement>();
  }

  private createRefHtmlLi(): RefObject<HTMLLIElement> {
    return createRef<HTMLLIElement>();
  }

  private getShortcuts(): LinkPropsDashboard[] {
    return [
      {
        value: 'beneficiario.inclusao',
        icon: '/static/assets/DLS_PersonPortraitAdd_40.svg',
        module: 'inclusao-beneficiario',
        twsFunction: '9126_14',
      },
      {
        value: 'movimentacao.lote',
        icon: '/static/assets/DLS_Upload_32.svg',
        module: 'movimentacao-lote',
        twsFunction: '9126_15',
      },
      {
        value: 'beneficiario.exclusao',
        icon: '/static/assets/DLS_PatientCancel_40.svg',
        module: 'exclusao-beneficiario',
        twsFunction: '9126_9',
      },
      {
        value: '$expression.$617370',
        icon: '/static/assets/DLS_PersonEdit_40.svg',
        module: 'alteracao-beneficiario',
      },
      {
        value: '$expression.$1032298',
        icon: '/static/assets/DLS_PaperNote_24.svg',
        module: 'dossie-contrato',
      },
      {
        value: 'stipulator.analise-atendimentos',
        icon: '/static/assets/DLS_ArchiveWarning_32.svg',
        module: 'analise-atendimentos',
        twsFunction: '9122_7',
      },
      {
        value: 'stipulator.historico-autorizacao',
        icon: '/static/assets/DLS_ImageInfoSeries_48.svg',
        module: 'historico-autorizacoes',
        twsFunction: '9122_1',
      },
      {
        value: 'stipulator.dossie-beneficiario',
        icon: '/static/assets/DLS_PersonProfile_48.svg',
        module: 'dossie-beneficiario',
        twsFunction: '9122_8',
      },
      {
        value: 'stipulator.historico-requisicoes',
        icon: '/static/assets/DLS_ExamCard_48.svg',
        module: 'historicos-requisicao',
        twsFunction: '9122_2',
      },
     
      {
        value: 'stipulator.consultar-alteracoes',
        icon: '/static/assets/DLS_ArchiveSearch_32.svg',
        module: 'historico-solicitacoes',
        twsFunction: 'historico-solicitacoes',
      },
      {
        value: 'stipulator.privacidade',
        icon: '/static/assets/DLS_LockPatient_48.svg',
        route: '/privacidade',
        requiredCount: true,
      },
      {
        value: this.expressaoRelatorio,
        icon: '/static/assets/DLS_Print_32.svg',
        route: '/relatorios',
        requiredCount: true,
      },
      {
        value: '$expression.$962143',
        icon: '/static/assets/DLS_PersonPortraitWrench_48.svg',
        route: '/meus-dados',
        requiredCount: true,
      },
    ];
  }

  private isPJ(): boolean {
    return StipulatorContext.isInitiated() ?  StipulatorContext.getCurrentContract().ieTipoEstipulante === IeTipoEstipulante.PJ: false;
  }

}
